// src/components/admin/AdminProducts.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useApp } from '../../lib/AppContext';
import ImageUploader from './ImageUploader';

export default function AdminProducts() {
  const navigate = useNavigate();
  const { showToast } = useApp();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  // Track which product has the image panel open: null | product.id
  const [imageEditId, setImageEditId] = useState(null);
  const [imageEditUrl, setImageEditUrl] = useState('');
  const [uploadError, setUploadError] = useState('');

  useEffect(() => {
    supabase.from('products').select('*, category:categories(name)').order('sort_order')
      .then(({ data }) => { setProducts(data || []); setLoading(false); });
  }, []);

  const toggleActive = async (id, current) => {
    await supabase.from('products').update({ active: !current }).eq('id', id);
    setProducts(p => p.map(x => x.id === id ? { ...x, active: !current } : x));
    showToast(current ? 'Product hidden' : 'Product visible', 'success');
  };

  const openImageEdit = (product) => {
    setImageEditId(product.id);
    setImageEditUrl(product.images?.[0] || '');
    setUploadError('');
  };

  const closeImageEdit = () => {
    setImageEditId(null);
    setImageEditUrl('');
    setUploadError('');
  };

  const saveImage = async (productId) => {
    // images column is an array — update index 0 (primary image)
    const product = products.find(p => p.id === productId);
    const updatedImages = [imageEditUrl, ...(product.images || []).slice(1)];
    const { error } = await supabase
      .from('products')
      .update({ images: updatedImages })
      .eq('id', productId);
    if (error) { showToast('Save failed: ' + error.message, 'error'); return; }
    setProducts(p => p.map(x => x.id === productId ? { ...x, images: updatedImages } : x));
    showToast('Product image updated', 'success');
    closeImageEdit();
  };

  const filtered = filter === 'all' ? products
    : filter === 'active' ? products.filter(p => p.active)
    : products.filter(p => !p.active);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
      <div className="spinner" />
    </div>
  );

  return (
    <div className="page-container" style={{ padding: '32px 24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <h2 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: '1.8rem', letterSpacing: '.05em' }}>
          Products ({products.length})
        </h2>
        <button className="btn-primary" style={{ padding: '9px 20px', fontSize: '.82rem' }}
          onClick={() => navigate('/admin/products/new')}>
          + Add Product
        </button>
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {['all', 'active', 'hidden'].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{
            padding: '6px 16px', border: 'none', borderRadius: 20,
            background: filter === f ? 'rgba(76,202,116,.15)' : '#111',
            color: filter === f ? '#4cca74' : '#555',
            fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.78rem',
            fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', cursor: 'pointer',
          }}>
            {f} ({f === 'all' ? products.length : f === 'active' ? products.filter(p => p.active).length : products.filter(p => !p.active).length})
          </button>
        ))}
      </div>

      {/* Product list */}
      <div style={{ background: '#111', borderRadius: 16, border: '1px solid rgba(255,255,255,.07)', overflow: 'hidden' }}>
        {filtered.map(p => (
          <div key={p.id}>
            {/* Product row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 20px', borderBottom: '1px solid rgba(255,255,255,.04)' }}>
              {/* Thumbnail — click to open image editor */}
              <button
                onClick={() => imageEditId === p.id ? closeImageEdit() : openImageEdit(p)}
                title="Edit image"
                style={{
                  width: 48, height: 48,
                  background: '#1a1a1a', borderRadius: 10,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '1.3rem', flexShrink: 0, overflow: 'hidden',
                  border: imageEditId === p.id ? '2px solid #4cca74' : '2px solid transparent',
                  cursor: 'pointer', padding: 0, transition: 'border-color .2s',
                }}
              >
                {p.images?.[0]
                  ? <img src={p.images[0]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <span>🌿</span>
                }
              </button>

              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.9rem', fontWeight: 700,
                  color: p.active ? '#f4f4f4' : '#555',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {p.name?.en || 'Unnamed'}
                </div>
                <div style={{ display: 'flex', gap: 10, marginTop: 2 }}>
                  <span style={{ fontSize: '.75rem', color: '#4cca74', fontWeight: 700 }}>€{p.price_eur}</span>
                  <span style={{ fontSize: '.75rem', color: p.stock > 5 ? '#4cca74' : p.stock > 0 ? '#f0a500' : '#e05050' }}>
                    {p.stock} in stock
                  </span>
                  <span style={{
                    fontSize: '.65rem', color: '#444',
                    fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: '.06em',
                  }}>
                    🖼 tap image to edit
                  </span>
                </div>
              </div>

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <button
                  onClick={() => toggleActive(p.id, p.active)}
                  style={{
                    background: p.active ? 'rgba(76,202,116,.12)' : 'rgba(255,255,255,.05)',
                    border: 'none', borderRadius: 20, padding: '5px 12px',
                    fontSize: '.7rem', fontWeight: 700,
                    color: p.active ? '#4cca74' : '#555',
                    cursor: 'pointer',
                    fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: '.06em', textTransform: 'uppercase',
                  }}
                >
                  {p.active ? 'Live' : 'Hidden'}
                </button>
                <button
                  onClick={() => navigate(`/admin/products/${p.id}`)}
                  style={{ background: 'rgba(255,255,255,.05)', border: 'none', borderRadius: 20, padding: '5px 14px', fontSize: '.75rem', color: '#888', cursor: 'pointer' }}
                >
                  Edit
                </button>
              </div>
            </div>

            {/* ── Inline image editor panel ── */}
            {imageEditId === p.id && (
              <div style={{
                background: '#0d0d0d',
                borderBottom: '1px solid rgba(255,255,255,.04)',
                padding: '16px 20px',
              }}>
                <div style={{
                  fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.7rem',
                  fontWeight: 700, letterSpacing: '.18em', textTransform: 'uppercase',
                  color: '#4cca74', marginBottom: 12,
                }}>
                  Product Image — {p.name?.en}
                </div>

                {/* Upload error */}
                {uploadError && (
                  <div className="upload-error">
                    {uploadError}
                    <code>
                      In Supabase: Storage → New bucket → Name: "products" → Public: ✓ → Create
                    </code>
                  </div>
                )}

                <ImageUploader
                  bucket="products"
                  folder="product-images"
                  value={imageEditUrl}
                  onChange={(url) => { setImageEditUrl(url); setUploadError(''); }}
                  onError={(msg) => setUploadError(msg)}
                  label="Upload Product Image"
                  preview="square"
                />

                {/* Save / Cancel */}
                <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
                  <button
                    className="btn-primary"
                    onClick={() => saveImage(p.id)}
                    style={{ minHeight: 44, minWidth: 120 }}
                  >
                    Save Image
                  </button>
                  <button
                    onClick={closeImageEdit}
                    style={{
                      background: 'none', border: '1px solid rgba(255,255,255,.1)',
                      borderRadius: 25, padding: '10px 20px', color: '#555',
                      fontFamily: "'Barlow Condensed',sans-serif", fontSize: '.82rem',
                      fontWeight: 700, cursor: 'pointer', minHeight: 44,
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}